import ContactLink from "@/components/ContactLink";
/**
 * FAQ entries grouped by role. Used by the role landing page.
 *
 * Keep answers tight — link out to a full article when something
 * needs more space. Markdown-style strong tags are rendered by the
 * Faq component.
 */

import React from "react";
import { Link } from "@inertiajs/react";

const A = ({ href, children }) => (
  <Link href={href}>{children}</Link>
);

export const FAQS = {
  school_admins: [
    {
      q: "Do we have to wait to be approved?",
      a: (
        <>
          <p>
            Not to get started. Signing up creates your school's account straight away, on a free trial, so you can set up your campaign and see how everything works. See <A href="/help/articles/getting-started-school-admin">Getting your school set up</A>.
          </p>
          <p>
            Before a campaign can go live, its flat fee needs to be paid by e-Transfer. We approve the campaign when the payment arrives. If it's been more than two business days since you sent it, email <ContactLink>hello@pagepledge.com</ContactLink> and we'll dig in. See <A href="/help/articles/paying-for-your-campaign">Paying for your campaign</A>.
          </p>
        </>
      ),
    },
    {
      q: "Why do I need a Square account?",
      a: (
        <>
          <p>
            So donations go straight into your school's bank account — they never pass through PagePledge. Your finance person almost certainly knows Square already; it's what most Canadian schools, PTAs, and small charities use.
          </p>
          <p>
            Walkthrough: <A href="/help/articles/connecting-square">Connecting Square</A>.
          </p>
        </>
      ),
    },
    {
      q: "Can I run more than one campaign at the same time?",
      a: (
        <p>
          No — campaigns can't overlap. A new one can start the day after the last one wraps up. See <A href="/help/articles/campaign-lifecycle">The four stages of a campaign</A>.
        </p>
      ),
    },
    {
      q: "What does PagePledge actually charge?",
      a: (
        <p>
          Schools pay a flat fee per campaign, with an <strong>early adopter discount</strong> available — never a percentage of what you raise. You can start with a free trial to set up and explore; running a campaign costs the fee, paid before it goes live. Square's small card-processing fee (about 2.8% + 30¢ per donation) is separate, and donors can choose to cover it. More: <A href="/help/articles/fees-and-pricing">How fees and pricing work</A>.
        </p>
      ),
    },
    {
      q: "A teacher says their classroom isn't showing up. What now?",
      a: (
        <p>
          They need to be added to that specific classroom — just being a teacher at your school isn't enough. Open the classroom, click <strong>Edit classroom</strong>, and add them under "Teachers." See <A href="/help/articles/inviting-teachers">Inviting teachers</A>.
        </p>
      ),
    },
    {
      q: "Can I change the dates after the campaign starts?",
      a: (
        <p>
          Once it's started, the start and end dates are locked in. Everything else — goals, rewards, the student roster, prizes — stays editable until the campaign wraps up. See <A href="/help/articles/after-the-campaign">After the campaign ends</A>.
        </p>
      ),
    },
    {
      q: "A parent says they didn't get the receipt email. What do I do?",
      a: (
        <p>
          Receipts come from hello@pagepledge.com and arrive within seconds. If one's missing, it's almost always the parent's spam filter — ask them to search "pagepledge" in their inbox. Every donation also has a permanent receipt link that admins can resend if needed.
        </p>
      ),
    },
    {
      q: "How do I refund a donation?",
      a: (
        <p>
          Refunds happen <strong>in your Square dashboard</strong>, not in PagePledge — see <A href="/help/articles/refunds-via-square">Refunding a donation</A>. Once Square processes it, the donation updates in PagePledge automatically.
        </p>
      ),
    },
    {
      q: "Can I remove a student mid-campaign?",
      a: (
        <p>
          Yes, up until the read-a-thon wraps up. After that, the student list is frozen. If a student is just moving classrooms, edit their existing record instead of removing and re-adding them — otherwise their parents' bookmark will break.
        </p>
      ),
    },
    {
      q: "Is student data ever sold or used for ads?",
      a: (
        <p>
          No, never. We're hosted in Toronto, we only collect what's needed to run the read-a-thon, and we don't run ads or any outside trackers. Full details in our <A href="/privacy">privacy policy</A>.
        </p>
      ),
    },
  ],

  teachers: [
    {
      q: "I got a sign-in email but the link expired. What now?",
      a: (
        <p>
          Sign-in links last 24 hours. If one has expired, request another from the sign-in page with the same email. See <A href="/help/articles/getting-started-teacher">Getting started as a teacher</A>.
        </p>
      ),
    },
    {
      q: "I logged minutes for the wrong day. Can I fix it?",
      a: (
        <p>
          Yes — every reading log shows up in your classroom's activity feed with an <strong>Undo</strong> button. Undo and re-log. See <A href="/help/articles/undoing-minute-batches">Undoing a reading log</A>.
        </p>
      ),
    },
    {
      q: "What's the difference between a classroom reward and a school-wide one?",
      a: (
        <p>
          A school-wide reward unlocks for the whole school when the whole school hits a number — pizza party at $1,000, that kind of thing. A classroom reward unlocks for just your room when your room hits a number. You set the classroom ones; your school admin sets the school-wide ones.
        </p>
      ),
    },
    {
      q: "What's the difference between a goal and a reward?",
      a: (
        <p>
          A goal is just a target with a progress bar — nothing's earned when you hit it, it's just a nudge. A reward is a target with something attached, like pajama day or extra recess, that everyone in the room earns together when you reach it. See <A href="/help/articles/setting-classroom-goals-and-milestones">Classroom goals and rewards</A>.
        </p>
      ),
    },
    {
      q: "Can I see how other classrooms are doing?",
      a: (
        <p>
          You'll see each classroom's totals on the school dashboard. Student names by classroom only show for your own classrooms; other rooms are summarised.
        </p>
      ),
    },
    {
      q: "A student earned a prize but I haven't given it to them yet — will it remember?",
      a: (
        <p>
          Yes. Earning and handing out are tracked separately. Your dashboard keeps a "waiting for their prize" list until you mark each one as handed out. See <A href="/help/articles/handing-out-student-prizes">Handing out student prizes</A>.
        </p>
      ),
    },
    {
      q: "Can I change my classroom's name?",
      a: (
        <p>
          Yes — open the classroom and click <strong>Edit classroom</strong>. Name, grade, and the other teachers in your room all live there.
        </p>
      ),
    },
    {
      q: "Can teachers see donation amounts or fees?",
      a: (
        <p>
          You'll see how much your classroom has raised in total. The donation-by-donation list and the fee math stay with school admins.
        </p>
      ),
    },
  ],

  parents: [
    {
      q: "I lost my child's take-home sheet. What now?",
      a: (
        <p>
          Ask the teacher or school admin to swap the activation code and reprint the sheet. The old code stops working as soon as they do. See <A href="/help/articles/rotating-activation-codes">Replacing a lost take-home sheet</A>.
        </p>
      ),
    },
    {
      q: "Do I have to make a password?",
      a: (
        <p>
          No — there are no passwords on PagePledge. To sign in, type your email and click the link we send to your inbox. See <A href="/help/articles/parent-magic-link-signin">Signing in</A>.
        </p>
      ),
    },
    {
      q: "Both parents want to follow the same child. How?",
      a: (
        <p>
          You can both sign up using the same activation code. Each of you will have your own account, and you'll both see the same child. The same trick works for grandparents — pass the sheet on. See <A href="/help/articles/multiple-children">Adding more than one child</A>.
        </p>
      ),
    },
    {
      q: "What's shown publicly about my child?",
      a: (
        <p>
          The donation page shows their first name and last initial, classroom, grade, school, and progress. No photos, no birthdate, no address — and the page address is a long random web link, so strangers can't guess at it. <A href="/help/articles/leaderboards-and-privacy">Leaderboards and your child's privacy</A> covers exactly who sees what.
        </p>
      ),
    },
    {
      q: "I logged the wrong number of minutes. Can I fix it?",
      a: (
        <p>
          Yes — within 24 hours, every log on your child's page has an undo button. After 24 hours, just message the teacher and they can fix it for you. See <A href="/help/articles/logging-minutes-at-home">Logging reading from home</A>.
        </p>
      ),
    },
    {
      q: "Can I donate to my own child?",
      a: (
        <p>
          Yes — use the same donation page you'd send to anyone else. Your donation will show up on your child's page just like a grandparent's would.
        </p>
      ),
    },
    {
      q: "When can I start donating?",
      a: (
        <p>
          Usually as soon as you get your take-home sheet, so grandparents and family have time to chip in before reading begins. Some schools pick a set date instead; if so, it's on your child's page. Reading minutes only start counting when the read-a-thon officially begins.
        </p>
      ),
    },
    {
      q: "What happens after the read-a-thon ends?",
      a: (
        <p>
          Donations stay open a little longer (usually a week) to catch any late chip-ins. After that, the final results are set in stone — but the page stays up, so you can always come back and see how everyone did.
        </p>
      ),
    },
  ],

  donors: [
    {
      q: "Do I need to make an account to donate?",
      a: (
        <p>
          No. Just open the link the family shared, pick an amount, enter your card. The receipt comes by email. See <A href="/help/articles/donating-without-an-account">Donating without making an account</A>.
        </p>
      ),
    },
    {
      q: "Where does my money actually go?",
      a: (
        <p>
          Straight to the school's bank account through Square. PagePledge never holds it. The school sees your donation right away.
        </p>
      ),
    },
    {
      q: "What's the \"cover the processing fee\" box?",
      a: (
        <p>
          Card transactions cost a small fee (about 2.8% + 30¢). If you check the box, you cover that fee on top of your donation, and the school gets the full amount you pledged. If you uncheck it, the school still gets your donation — the fee just comes off the top. See <A href="/help/articles/covering-processing-fees">Covering the processing fee</A>.
        </p>
      ),
    },
    {
      q: "Can I donate anonymously?",
      a: (
        <p>
          Yes — check the anonymous box at checkout and your name shows as "Anonymous" on the public page. The school will still see your real name in their records, so they can help you out if you ever need a refund. See <A href="/help/articles/anonymous-donations-and-messages">Anonymous donations and messages</A>.
        </p>
      ),
    },
    {
      q: "Will I get a tax receipt?",
      a: (
        <>
          <p>
            No. We'll email you a receipt for your records, but it isn't a charitable tax receipt.
          </p>
        </>
      ),
    },
    {
      q: "I made a mistake — can I get a refund?",
      a: (
        <p>
          Yes. Email the school (their address is on your receipt) and they'll refund you. The money usually shows back up on your card within 5–10 days. See <A href="/help/articles/donor-receipts-and-refunds">Receipts and refunds</A>.
        </p>
      ),
    },
    {
      q: "I never got my receipt.",
      a: (
        <p>
          Search your inbox for "pagepledge" — receipts come from hello@pagepledge.com. If it's still missing, check your spam folder. The receipt link on the thank-you page after donating is permanent — you can come back to it any time.
        </p>
      ),
    },
  ],
};
